import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { boardApi } from '@/api/endpoints';
import { Alert, Empty, ErrorState, Loading } from '@/components/common/State';
import Avatar from '@/components/common/Avatar';
import MediaAttach, { MediaThumbs } from '@/components/common/MediaAttach';
import { useMediaUpload } from '@/components/common/useMediaUpload';
import { toMediaInputs, type DraftMedia } from '@/utils/media';
import { POST_STATUS_LABEL, formatDate } from '@/utils/format';
import { useAuthStore } from '@/store/authStore';

export default function BoardDetailPage() {
  const { postId } = useParams();
  const [searchParams] = useSearchParams();
  const id = Number(postId);
  // 조회에 실패하면 본문이 없어 게시판 종류를 알 수 없다. 목록에서 넘어올 때 실어 준 값을 쓴다.
  const listUrl = `/boards?boardType=${searchParams.get('boardType') ?? 'QNA'}`;
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [comment, setComment] = useState('');
  const [commentMedia, setCommentMedia] = useState<DraftMedia[]>([]);
  const uploader = useMediaUpload('boards');
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['post', id],
    queryFn: () => boardApi.detail(id),
    enabled: Number.isFinite(id),
    retry: false,
  });

  const addComment = useMutation({
    mutationFn: async () => {
      const uploaded = await uploader.upload(commentMedia);
      setCommentMedia(uploaded);
      return boardApi.addComment(id, { content: comment, media: toMediaInputs(uploaded) });
    },
    onSuccess: () => {
      setComment('');
      setCommentMedia([]);
      queryClient.invalidateQueries({ queryKey: ['post', id] });
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  const remove = useMutation({
    mutationFn: () => boardApi.remove(id),
    onSuccess: () => {
      window.location.href = data?.boardType ? `/boards?boardType=${data.boardType}` : listUrl;
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  if (isLoading) return <Loading />;
  if (isError) {
    // 비밀글 권한 없음처럼 재시도해도 소용없는 경우가 있어 목록으로 빠져나갈 길을 함께 둔다.
    return (
      <ErrorState error={error} onRetry={refetch}>
        <Link to={listUrl} className="btn btn--primary btn--sm">
          목록으로
        </Link>
      </ErrorState>
    );
  }
  if (!data) return <Empty title="게시글을 찾을 수 없습니다." />;

  return (
    <>
      <div className="card">
        <div className="card__body">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="badge">{data.boardType === 'QNA' ? '상품문의' : 'VoC'}</span>
            {data.category && <span className="badge badge--muted">{data.category}</span>}
            <span className={`badge badge--${data.status === 'ANSWERED' ? 'success' : 'muted'}`}>
              {POST_STATUS_LABEL[data.status]}
            </span>
          </div>

          <h1 style={{ fontSize: 22, margin: '14px 0 8px' }}>
            {data.secret && '🔒 '}
            {data.title}
          </h1>
          <p className="muted writer" style={{ marginTop: 0 }}>
            <Avatar src={data.writerProfileImageUrl} size={28} />
            {data.writer} · {formatDate(data.createdAt, true)} · 조회 {data.viewCount}
          </p>

          {data.productName && (
            <p>
              문의 상품:{' '}
              <Link to={`/products/${data.productId}`}>
                <strong>{data.productName}</strong>
              </Link>
            </p>
          )}

          <hr style={{ border: 0, borderTop: '1px solid var(--gray-200)', margin: '16px 0' }} />
          <p style={{ whiteSpace: 'pre-wrap', minHeight: 80 }}>{data.content}</p>
          <MediaThumbs media={data.media} size={120} />

          {user?.userId === data.userId && (
            <div style={{ textAlign: 'right' }}>
              <button
                type="button"
                className="btn btn--ghost btn--sm muted"
                onClick={() => remove.mutate()}
              >
                삭제
              </button>
            </div>
          )}
        </div>
      </div>

      <h2 style={{ fontSize: 17, marginTop: 28 }}>답변 {data.comments.length}</h2>
      {feedback && <Alert tone="error">{feedback}</Alert>}

      {data.comments.length === 0 && <Empty title="아직 답변이 없습니다." />}
      {data.comments.map((c) => (
        <div
          className="card"
          key={c.commentId}
          style={{ marginBottom: 10, borderLeft: c.answer ? '3px solid var(--brand-500)' : undefined }}
        >
          <div className="card__body">
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <strong className="writer">
                <Avatar src={c.writerProfileImageUrl} size={24} />
                {c.writer} {c.answer && <span className="badge badge--info">공식 답변</span>}
              </strong>
              <span className="muted">{formatDate(c.createdAt, true)}</span>
            </div>
            <p style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>{c.content}</p>
            <MediaThumbs media={c.media} />
          </div>
        </div>
      ))}

      {user && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card__body">
            <textarea
              className="textarea"
              placeholder="답변을 입력하세요"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <MediaAttach value={commentMedia} onChange={setCommentMedia} max={3} progress={uploader.progress} />
            <div style={{ textAlign: 'right', marginTop: 10 }}>
              <button
                type="button"
                className="btn btn--primary"
                disabled={!comment.trim() || addComment.isPending}
                onClick={() => addComment.mutate()}
              >
                등록
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <Link to={`/boards?boardType=${data.boardType}`} className="btn btn--outline btn--sm">
          목록으로
        </Link>
      </div>
    </>
  );
}
